import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { IndianRupee } from 'lucide-react';
import api from '../api';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';
import Modal from '../components/Modal';
import CreditRechargeModal from '../components/credits/CreditRechargeModal';
import ProfileCompletion from '../components/ProfileCompletion';
import { SubscriptionStatus } from '../components/subscriptions';

const CreatorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeProjects, setActiveProjects] = useState([]);
  const [totalActiveProjects, setTotalActiveProjects] = useState(0);
  const [connectedBrands, setConnectedBrands] = useState([]);
  const [totalConnectedBrands, setTotalConnectedBrands] = useState(0);
  const [biddingOpportunities, setBiddingOpportunities] = useState([]);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreditRechargeModal, setShowCreditRechargeModal] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(true);
  
  // Profile management modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    profilePictureUrl: '',
    website: '',
    contactNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    instagramHandle: '',
    twitterHandle: '',
    facebookHandle: '',
    youtubeHandle: '',
    tiktokHandle: ''
  });
  const [profileMessage, setProfileMessage] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'creator') return;

    const fetchDashboardData = async () => {
      setLoading(true);
      const maxRetries = 3;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          setRetryCount(attempt - 1);
          const response = await api.get(`/creators/${user.userId}/dashboard`);
          const data = response.data;
          
          // Ensure data properties exist and are arrays before setting state
          setActiveProjects(Array.isArray(data.activeProjects) ? data.activeProjects : []);
          setTotalActiveProjects(typeof data.totalActiveProjects === 'number' ? data.totalActiveProjects : 0);
          setConnectedBrands(Array.isArray(data.connectedBrands) ? data.connectedBrands : []);
          setTotalConnectedBrands(typeof data.totalConnectedBrands === 'number' ? data.totalConnectedBrands : 0);
          setBiddingOpportunities(Array.isArray(data.biddingOpportunities) ? data.biddingOpportunities : []);
          setCredits(typeof data.credits === 'number' ? data.credits : 0);
          
          setDataLoaded(true);
          
          // Also fetch profile data
          try {
            const profileResponse = await api.get(`/users/${user.userId}/profile/manage`);
            setProfile(prevProfile => ({ ...prevProfile, ...profileResponse.data }));
          } catch (profileErr) {
            // Keep default empty profile if fetch fails
          }
          
          // If we reach here, data was successfully loaded
          setLoading(false);
          return; // Exit the retry loop
          
        } catch (err) {

          if (attempt === maxRetries) {
            // Final attempt failed
            setActiveProjects([]);
            setConnectedBrands([]);
            setBiddingOpportunities([]);
            setCredits(0);
            setLoading(false);
          } else {
            // Wait before retrying (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    };

    fetchDashboardData();
  }, [user]);

  const handleSubmitContent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post(`/projects/${selectedProject.id}/submit`, formData);
      alert('Submitted successfully!');
      setShowSubmitModal(false);
    } catch {
      alert('Submission failed.');
    }
  };

  // Profile management functions
  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleProfileSave = async () => {
    try {
      await api.put(`/users/${user.userId}/profile/manage`, profile);
      setProfileMessage('Profile updated successfully!');
      setTimeout(() => {
        setProfileMessage('');
        setShowProfileModal(false);
      }, 2000);
    } catch {
      setProfileMessage('Update failed.');
    }
  };

  const openProfileModal = async () => {
    setProfileMessage('');
    
    // Fetch latest profile data when opening modal
    try {
      const profileResponse = await api.get(`/users/${user.userId}/profile/manage`);
      setProfile(prevProfile => ({ ...prevProfile, ...profileResponse.data }));
    } catch (profileErr) {
      // Error handled silently
    }
    
    setShowProfileModal(true);
  };

  const handleRechargeSuccess = (rechargeData) => {
    setCredits(rechargeData.totalCredits);
    setShowCreditRechargeModal(false);
    // You could also show a success message here
  };

  if (!user) return <p className="p-8 text-center">Please log in to view your dashboard.</p>;
  if (user.role !== 'creator') return <p className="p-8 text-center">Access denied. Only Creators can view this page.</p>;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Dashboard</h3>
          <p className="text-gray-600">
            {retryCount > 0 
              ? `Retrying connection... (attempt ${retryCount + 1}/3)`
              : 'Fetching your dashboard data...'
            }
          </p>
          {retryCount > 0 && (
            <p className="text-sm text-blue-600 mt-2">
              ⚡ Using NgRok tunnel - this may take a moment
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <CreatorNavbar />
      <div className="dashboard-shell pt-20 sm:pt-24 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <CreatorPageHeader
          kicker="Creator overview"
          title={`Welcome${user?.name ? ` ${user.name.split(' ')[0]}` : ''}!`}
          description="Manage your creator activity, track active projects, and keep your next campaign move clear."
          actions={
            <>
            {/* Manage Profile Button - DISABLED */}
            {/* Can be re-enabled in the future if needed */}
            {/*
            <button
              onClick={openProfileModal}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="hidden sm:inline">Manage</span> Profile
            </button>
            */}
              <button
                onClick={() => navigate(`/creator/${user.userId}`)}
                className="primary-button !px-5 !py-2.5 !text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="hidden sm:inline">View</span> Public
              </button>
            </>
          }
        />

        {/* Profile Completion Section */}
        {showProfileCompletion && (
          <ProfileCompletion onClose={() => setShowProfileCompletion(false)} />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="metric-card">
            <p className="text-sm text-slate-500">Active projects</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{totalActiveProjects}</p>
            <p className="mt-2 text-sm text-slate-500">Campaigns currently in motion.</p>
          </div>
          <div className="metric-card">
            <p className="text-sm text-slate-500">Connected brands</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{totalConnectedBrands}</p>
            <p className="mt-2 text-sm text-slate-500">Brands already collaborating with you.</p>
          </div>
          <div className="metric-card">
            <p className="text-sm text-slate-500">Open opportunities</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{biddingOpportunities.length}</p>
            <p className="mt-2 text-sm text-slate-500">Live bids and new campaign chances.</p>
          </div>
          <div className="metric-card">
            <p className="text-sm text-slate-500">Credits balance</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{credits}</p>
            <p className="mt-2 text-sm text-slate-500">Available for future paid workflow steps.</p>
          </div>
        </div>

        {/* Subscription Status - DISABLED */}
        {/* Temporarily disabled - can be re-enabled in the future */}
        {/* <SubscriptionStatus /> */}

        {/* Quick Navigation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            onClick={() => navigate('/dashboard/creator/live-campaigns')}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 rounded-xl shadow-lg cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">Live Campaigns</h3>
                <p className="text-xs sm:text-sm opacity-90">Browse & bid on active campaigns</p>
              </div>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          
          <div 
            onClick={() => navigate('/dashboard/creator/projects')}
            className="bg-[linear-gradient(135deg,#539ECB_0%,#3C86B5_100%)] text-white p-4 sm:p-6 rounded-xl shadow-lg cursor-pointer transition-all duration-200 transform hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">My Projects</h3>
                <p className="text-xs sm:text-sm opacity-90">View all your projects</p>
              </div>
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-2l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          <div 
            onClick={() => navigate('/dashboard/creator/bids')}
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 sm:p-6 rounded-xl shadow-lg cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">My Bids</h3>
                <p className="text-xs sm:text-sm opacity-90">Track your bid status</p>
              </div>
              <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>

          <div
            onClick={() => navigate('/dashboard/creator/collaborator-club')}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 sm:p-6 rounded-xl shadow-lg cursor-pointer hover:from-orange-600 hover:to-orange-700 transition-all duration-200 transform hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">Creator Hub</h3>
                <p className="text-xs sm:text-sm opacity-90">Find and manage creator collaborators</p>
              </div>
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5V4H2v16h5m10 0v-2a4 4 0 00-8 0v2m8 0H9m4-9a4 4 0 100-8 4 4 0 000 8z" />
              </svg>
            </div>
          </div>
        </div>

         {/* Credits Section - DISABLED */}
        {/* Temporarily disabled - can be re-enabled in the future */}
        {/*
        <section className="app-card p-4 sm:p-6 rounded-[1.75rem]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">Your Credits</h2>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-2xl sm:text-3xl font-bold text-blue-600">{credits}</span>
                <span className="text-sm text-gray-600">Available Credits</span>
              </div>
            </div>
            <button
              onClick={() => setShowCreditRechargeModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Recharge Credits
            </button>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-3">
            You need 10 credits to place a bid on any campaign. Recharge when running low!
          </p>
        </section>
        */}

        {/* Active Projects */}
        <section className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">
              Active Projects {totalActiveProjects > 0 && <span className="text-sm text-gray-500 font-normal">({totalActiveProjects} total)</span>}
            </h2>
            {totalActiveProjects > 5 && (
              <button
                onClick={() => navigate('/dashboard/creator/projects')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
              >
                View All
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
          {(activeProjects && activeProjects.length > 0) ? (
            activeProjects.map((project) => (
              <div key={project.id} className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100 gap-3">
                <div className="flex items-center gap-3 sm:gap-4">
                  <img src={project.brandLogoUrl} alt={project.brandName} className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover flex-shrink-0" />
                  <div>
                    <div className="font-semibold">{project.title}</div>
                    <div className="text-sm text-gray-500">{project.brandName}</div>
                    {project.deadline && <div className="text-xs text-red-500">Deadline: {project.deadline}</div>}
                    {project.budget && <div className="text-xs text-green-500">Budget: ₹{project.budget?.toLocaleString('en-IN')}</div>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-sm text-blue-500">{project.status}</span>
                  {project.completedBycreator ? (
                    <span className="text-sm bg-collab-accent-soft text-collab-shadow px-3 py-1 rounded-full font-medium flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Completed
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowSubmitModal(true);
                        }}
                        className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Submit Content
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to mark this campaign as completed? The brand will be able to review your work.')) {
                            return;
                          }
                          try {
                            await api.post(`/campaigns/${project.id}/complete`);
                            alert('Campaign marked as completed successfully!');
                            window.location.reload();
                          } catch (error) {
                            alert(error.response?.data?.error || 'Failed to mark campaign as complete');
                          }
                        }}
                        className="text-sm bg-collab-accent text-white px-3 py-1 rounded hover:brightness-95 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark as Complete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500">No active projects yet.</p>
          )}
        </section>

        {/* Connected Brands */}
        <section className="app-card p-6 rounded-[1.75rem]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Connected Brands {totalConnectedBrands > 0 && <span className="text-sm text-gray-500 font-normal">({totalConnectedBrands} total)</span>}
            </h2>
            {totalConnectedBrands > 5 && (
              <button
                onClick={() => navigate('/dashboard/creator/projects')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
              >
                View All
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
          {(connectedBrands && connectedBrands.length > 0) ? (
            <div className="space-y-3">
              {connectedBrands.map((brand) => {
                // Generate a color based on brand name
                const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-yellow-500', 'bg-red-500'];
                const colorIndex = (brand.name?.charCodeAt(0) || 0) % colors.length;
                const bgColor = colors[colorIndex];

                return (
                  <div key={brand.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white transition-colors">
                    <div className="flex items-center space-x-3">
                      {brand.brandLogoUrl ? (
                        <img
                          src={brand.brandLogoUrl}
                          alt={brand.name}
                          className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className={`h-10 w-10 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white font-bold text-lg">
                            {brand.name?.charAt(0).toUpperCase() || 'B'}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-gray-900">{brand.name}</div>
                        {brand.totalBudget && (
                          <div className="text-xs text-gray-500">
                            Total Budget: ₹{brand.totalBudget?.toLocaleString('en-IN')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500">You are not connected to any brands.</p>
          )}
        </section>

        {/* Bidding Opportunities Section - REMOVED */}
        {/* Creators can find campaigns in Live Campaigns page */}

        {/* Credits Section - DISABLED */}
        {/* Temporarily disabled - can be re-enabled in the future */}
        {/*
        <section className="bg-white p-6 rounded-xl shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Your Credits</h2>
            <button
              onClick={() => setShowCreditRechargeModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Recharge Credits
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-blue-600">{credits}</p>
              <p className="text-sm text-gray-600">Available Credits</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Credit Value</p>
              <p className="text-lg font-semibold">₹20 per credit</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Each bid costs 10 credits. Get discounts on bulk purchases!
            </p>
          </div>
        </section>
        */}
        </div>
      </div>

      {/* Submit Content Modal */}
      {showSubmitModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Submit Content for {selectedProject.title}</h3>
            <form onSubmit={handleSubmitContent}>
              <input type="file" name="file" required className="mb-4 block w-full" />
              <textarea
                name="notes"
                placeholder="Add any notes..."
                className="w-full border rounded p-2 mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="text-gray-600 hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Management Modal */}
      <Modal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)}>
        <div className="max-w-lg">
          <h3 className="text-xl font-bold mb-4">Manage Your Profile</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            <div>
              <label className="block font-semibold text-sm mb-1">Bio</label>
              <textarea
                name="bio"
                value={profile.bio}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Short bio"
                rows="3"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Profile Picture URL</label>
              <input
                name="profilePictureUrl"
                value={profile.profilePictureUrl}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Profile picture URL"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Website</label>
              <input
                name="website"
                value={profile.website}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Website"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Contact Number</label>
              <input
                name="contactNumber"
                value={profile.contactNumber}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Contact Number"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Address Line 1</label>
              <input
                name="addressLine1"
                value={profile.addressLine1}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Address Line 1"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Address Line 2</label>
              <input
                name="addressLine2"
                value={profile.addressLine2}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Address Line 2 (Optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-sm mb-1">City</label>
                <input
                  name="city"
                  value={profile.city}
                  onChange={handleProfileChange}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block font-semibold text-sm mb-1">State</label>
                <input
                  name="state"
                  value={profile.state}
                  onChange={handleProfileChange}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="State"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-sm mb-1">Postal Code</label>
                <input
                  name="postalCode"
                  value={profile.postalCode}
                  onChange={handleProfileChange}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Postal Code"
                />
              </div>
              <div>
                <label className="block font-semibold text-sm mb-1">Country</label>
                <input
                  name="country"
                  value={profile.country}
                  onChange={handleProfileChange}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="Country"
                />
              </div>
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Instagram Handle</label>
              <input
                name="instagramHandle"
                value={profile.instagramHandle}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Instagram"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Twitter Handle</label>
              <input
                name="twitterHandle"
                value={profile.twitterHandle}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Twitter"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">Facebook Handle</label>
              <input
                name="facebookHandle"
                value={profile.facebookHandle}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="Facebook"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">YouTube Handle</label>
              <input
                name="youtubeHandle"
                value={profile.youtubeHandle}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="YouTube"
              />
            </div>
            <div>
              <label className="block font-semibold text-sm mb-1">TikTok Handle</label>
              <input
                name="tiktokHandle"
                value={profile.tiktokHandle}
                onChange={handleProfileChange}
                className="w-full border rounded p-2 text-sm"
                placeholder="TikTok"
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <div className="flex-1">
              {profileMessage && (
                <div className={`text-sm ${profileMessage.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                  {profileMessage}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-600 hover:underline px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleProfileSave}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Credit Recharge Modal - DISABLED */}
      {/* Temporarily disabled - can be re-enabled in the future */}
      {/*
      <CreditRechargeModal
        isOpen={showCreditRechargeModal}
        onClose={() => setShowCreditRechargeModal(false)}
        onRechargeSuccess={handleRechargeSuccess}
        userId={user?.userId}
        currentCredits={credits}
      />
      */}
    </>
  );
};

export default CreatorDashboard;
